// src/pages/LandingPage.jsx
import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { changeLanguage } from '../i18n/i18n';
import {
    CheckCircleIcon, MapPinIcon, BoltIcon,
    DocumentChartBarIcon, SparklesIcon, ShieldCheckIcon,
    GlobeAltIcon, BellAlertIcon, Bars3Icon, XMarkIcon,
    UsersIcon, DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline';

// ── Logo InfraPilot ──────────────────────────────────────────────────────────
const Logo = ({ size = 44 }) => (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
        <path d="M50 5 L90 25 L90 65 Q90 85 50 97 Q10 85 10 65 L10 25 Z" fill="#00c896" />
        <path d="M50 12 L84 30 L84 64 Q84 80 50 91 Q16 80 16 64 L16 30 Z" fill="#00e5b0" />
        <text x="28" y="58" fontFamily="Arial" fontWeight="bold" fontSize="32" fill="#0a0a0a" fontStyle="italic">i</text>
        <text x="42" y="62" fontFamily="Arial" fontWeight="bold" fontSize="36" fill="#0a0a0a">P</text>
    </svg>
);

const LANGS = [
    { code: 'it', flag: '🇮🇹', label: 'IT' },
    { code: 'fr', flag: '🇫🇷', label: 'FR' },
    { code: 'en', flag: '🇬🇧', label: 'EN' },
];

const LangBar = () => {
    const { i18n } = useTranslation();
    return (
        <div className="flex items-center gap-1">
            {LANGS.map(l => (
                <button key={l.code} onClick={() => changeLanguage(l.code)}
                    className="px-2.5 py-1 rounded-lg text-xs font-bold transition"
                    style={i18n.language === l.code
                        ? { backgroundColor: '#00e5b0', color: '#0a0a0a' }
                        : { color: '#888', backgroundColor: 'transparent' }}>
                    {l.flag} {l.label}
                </button>
            ))}
        </div>
    );
};

const FEATURE_ICONS = [
    DocumentChartBarIcon, BellAlertIcon, MapPinIcon,
    SparklesIcon, GlobeAltIcon, ShieldCheckIcon,
    UsersIcon, DevicePhoneMobileIcon, BoltIcon,
];

const PLANS_BASE = [
    { id: 'FREE',       name: 'Free',       icon: '🆓', priceEur: 0,   highlight: false, cta_key: 'Inizia gratis' },
    { id: 'STARTER',    name: 'Starter',    icon: '🚀', priceEur: 29,  highlight: false, cta_key: 'Prova 14 giorni' },
    { id: 'PRO',        name: 'Pro',        icon: '⚡', priceEur: 79,  highlight: true,  cta_key: 'Prova 14 giorni' },
    { id: 'ENTERPRISE', name: 'Enterprise', icon: '👑', priceEur: 149, highlight: false, cta_key: 'Contattaci' },
];

const PLAN_FEATURES = {
    FREE:       { features: ['3 progetti', '2 utenti', '100 spese/mese', 'Dashboard base'],       missing: ['AI Assistant', 'Report PDF', 'Alert budget'] },
    STARTER:    { features: ['10 progetti', '10 utenti', '500 spese/mese', '50 AI/mese', 'Report PDF', 'Alert budget'], missing: ['API Access', 'Multi-valuta'] },
    PRO:        { features: ['Progetti illimitati', 'Utenti illimitati', 'Spese illimitate', '200 AI/mese', 'API Access', 'Multi-valuta', 'Alert SMS + Email'], missing: [] },
    ENTERPRISE: { features: ['Tutto di Pro', '100 GB Storage', 'Supporto 24/7', 'White Label', 'Audit Logs', 'SLA garantito'], missing: [] },
};

// ─────────────────────────────────────────────────────────────────────────────
const LandingPage = () => {
    const { isAuthenticated } = useAuth();
    const { t, i18n } = useTranslation();
    const [menuOpen, setMenuOpen] = useState(false);
    const [billing, setBilling] = useState('monthly');

    if (isAuthenticated) return <Navigate to="/dashboard" replace />;

    const getPrice = (plan) => {
        if (plan.priceEur === 0) return t('plans.free');
        const price = billing === 'yearly' ? Math.round(plan.priceEur * 0.83) : plan.priceEur;
        return `€${price}`;
    };

    const featureKeys = ['f1','f2','f3','f4','f5','f6','f7','f8','f9'];

    return (
        <div className="min-h-screen font-sans" style={{ backgroundColor: '#0a0a0a', color: '#f0f0f0' }}>

            {/* ── NAVBAR ─────────────────────────────────────────────────── */}
            <nav style={{ backgroundColor: 'rgba(10,10,10,0.95)', borderBottom: '1px solid #1a1a1a' }}
                className="fixed top-0 left-0 right-0 z-50 backdrop-blur">
                <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Logo size={36} />
                        <span className="text-xl font-black text-white tracking-tight">InfraPilot</span>
                    </div>

                    <div className="hidden md:flex items-center gap-8 text-sm font-medium" style={{ color: '#888' }}>
                        <a href="#features" className="hover:text-white transition">{t('landing.features.title')}</a>
                        <a href="#how" className="hover:text-white transition">{t('landing.how.title')}</a>
                        <a href="#pricing" className="hover:text-white transition">{t('landing.pricing.title')}</a>
                        <a href="#regions" className="hover:text-white transition">{t('landing.regions.title')}</a>
                    </div>

                    <div className="hidden md:flex items-center gap-3">
                        <LangBar />
                        <Link to="/login" className="px-4 py-2 text-sm font-semibold transition" style={{ color: '#aaa' }}>
                            {t('auth.login')}
                        </Link>
                        <Link to="/register" className="px-5 py-2 text-sm font-bold rounded-xl transition"
                            style={{ backgroundColor: '#00e5b0', color: '#0a0a0a' }}>
                            {t('auth.register')} →
                        </Link>
                    </div>

                    <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden p-2" style={{ color: '#aaa' }}>
                        {menuOpen ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
                    </button>
                </div>

                {menuOpen && (
                    <div className="md:hidden px-6 pb-5 pt-3 space-y-3" style={{ borderTop: '1px solid #1a1a1a' }}>
                        <a href="#features" className="block text-sm py-2" style={{ color: '#aaa' }}>{t('landing.features.title')}</a>
                        <a href="#how" className="block text-sm py-2" style={{ color: '#aaa' }}>{t('landing.how.title')}</a>
                        <a href="#pricing" className="block text-sm py-2" style={{ color: '#aaa' }}>{t('landing.pricing.title')}</a>
                        <div className="py-2"><LangBar /></div>
                        <div className="flex gap-3 pt-2">
                            <Link to="/login" className="flex-1 py-2.5 text-center text-sm rounded-xl font-semibold"
                                style={{ border: '1px solid #333', color: '#aaa' }}>{t('auth.login')}</Link>
                            <Link to="/register" className="flex-1 py-2.5 text-center text-sm rounded-xl font-bold"
                                style={{ backgroundColor: '#00e5b0', color: '#0a0a0a' }}>{t('auth.register')}</Link>
                        </div>
                    </div>
                )}
            </nav>

            {/* ── HERO ───────────────────────────────────────────────────── */}
            <section className="pt-40 pb-28 px-6 relative overflow-hidden">
                <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full opacity-10 blur-3xl pointer-events-none"
                    style={{ backgroundColor: '#00e5b0' }} />
                <div className="max-w-5xl mx-auto text-center relative">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold mb-8"
                        style={{ border: '1px solid #1f3d35', backgroundColor: '#0d2420', color: '#00e5b0' }}>
                        <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#00e5b0' }} />
                        {t('landing.hero.badge')}
                    </div>
                    <div className="flex justify-center mb-8"><Logo size={90} /></div>
                    <h1 className="font-black leading-tight mb-6" style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)', letterSpacing: '-0.03em' }}>
                        {t('landing.hero.title1')}
                        <span className="block" style={{ color: '#00e5b0' }}>{t('landing.hero.title2')}</span>
                    </h1>
                    <p className="text-lg leading-relaxed max-w-2xl mx-auto mb-12" style={{ color: '#888' }}>
                        {t('landing.hero.subtitle')}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center mb-14">
                        <Link to="/register" className="px-8 py-4 text-lg font-black rounded-2xl transition shadow-lg"
                            style={{ backgroundColor: '#00e5b0', color: '#0a0a0a', boxShadow: '0 0 40px rgba(0,229,176,0.25)' }}>
                            {t('landing.hero.cta1')}
                        </Link>
                        <Link to="/login" className="px-8 py-4 text-lg font-semibold rounded-2xl transition"
                            style={{ border: '1px solid #2a2a2a', color: '#aaa' }}>
                            {t('landing.hero.cta2')}
                        </Link>
                    </div>
                    <div className="flex flex-wrap justify-center gap-6 text-sm" style={{ color: '#555' }}>
                        {['trust1','trust2','trust3','trust4'].map(k => (
                            <span key={k} className="flex items-center gap-2">
                                <CheckCircleIcon className="w-4 h-4" style={{ color: '#00e5b0' }} />
                                {t(`landing.hero.${k}`)}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── STATS BAR ──────────────────────────────────────────────── */}
            <section style={{ backgroundColor: '#111', borderTop: '1px solid #1a1a1a', borderBottom: '1px solid #1a1a1a' }} className="py-8">
                <div className="max-w-4xl mx-auto px-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                        {[
                            ['10+', t('landing.stats.countries')],
                            ['∞',   t('landing.stats.projects')],
                            ['4',   t('landing.stats.currencies')],
                            ['3',   t('landing.stats.languages')],
                        ].map(([val, label]) => (
                            <div key={label}>
                                <div className="text-3xl font-black mb-1" style={{ color: '#00e5b0' }}>{val}</div>
                                <div className="text-sm" style={{ color: '#555' }}>{label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── FEATURES ───────────────────────────────────────────────── */}
            <section id="features" className="py-28 px-6">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#00e5b0' }}>{t('landing.features.title')}</span>
                        <h2 className="text-4xl font-black mt-3 text-white">{t('landing.features.title')}</h2>
                        <p className="mt-3 max-w-xl mx-auto" style={{ color: '#666' }}>{t('landing.features.subtitle')}</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {featureKeys.map((k, i) => {
                            const Icon = FEATURE_ICONS[i];
                            return (
                                <div key={k} className="p-6 rounded-2xl transition"
                                    style={{ backgroundColor: '#111', border: '1px solid #1a1a1a' }}>
                                    <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                                        style={{ backgroundColor: '#0d2420' }}>
                                        <Icon className="w-5 h-5" style={{ color: '#00e5b0' }} />
                                    </div>
                                    <h3 className="font-bold text-white mb-2">{t(`landing.features.${k}t`)}</h3>
                                    <p className="text-sm leading-relaxed" style={{ color: '#666' }}>{t(`landing.features.${k}d`)}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ── COME FUNZIONA ──────────────────────────────────────────── */}
            <section id="how" className="py-28 px-6" style={{ backgroundColor: '#0d0d0d' }}>
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#00e5b0' }}>{t('landing.how.title')}</span>
                        <h2 className="text-4xl font-black mt-3 text-white">{t('landing.how.title')}</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {['s1','s2','s3','s4'].map((k, i) => (
                            <div key={k} className="relative">
                                {i < 3 && (
                                    <div className="hidden lg:block absolute top-8 left-full w-full h-px z-0"
                                        style={{ background: 'linear-gradient(to right, #1f3d35, transparent)' }} />
                                )}
                                <div className="relative z-10">
                                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-xl font-black mb-5"
                                        style={{ backgroundColor: '#0d2420', color: '#00e5b0', border: '1px solid #1f3d35' }}>
                                        {`0${i+1}`}
                                    </div>
                                    <h3 className="font-bold text-white mb-2">{t(`landing.how.${k}t`)}</h3>
                                    <p className="text-sm leading-relaxed" style={{ color: '#666' }}>{t(`landing.how.${k}d`)}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── REGIONI ────────────────────────────────────────────────── */}
            <section id="regions" className="py-28 px-6">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#00e5b0' }}>{t('landing.regions.title')}</span>
                        <h2 className="text-4xl font-black mt-3 text-white">{t('landing.regions.title')}</h2>
                        <p className="mt-3" style={{ color: '#666' }}>{t('landing.regions.subtitle')}</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {[
                            { flag: '🌍', name: 'Africa Occidentale', countries: ['Guinée 🇬🇳', 'Sénégal 🇸🇳', "Côte d'Ivoire 🇨🇮", 'Mali 🇲🇱'], payment: 'Wave · Orange Money · MTN MoMo', currency: 'GNF · XOF' },
                            { flag: '🇪🇺', name: 'Europa', countries: ['Italia 🇮🇹', 'France 🇫🇷', 'United Kingdom 🇬🇧', 'Deutschland 🇩🇪'], payment: 'Stripe · Carta di credito', currency: 'EUR · GBP' },
                        ].map((r) => (
                            <div key={r.name} className="p-8 rounded-3xl" style={{ backgroundColor: '#111', border: '1px solid #1a1a1a' }}>
                                <div className="text-5xl mb-4">{r.flag}</div>
                                <h3 className="text-2xl font-black text-white mb-4">{r.name}</h3>
                                <div className="flex flex-wrap gap-2 mb-5">
                                    {r.countries.map(c => (
                                        <span key={c} className="px-3 py-1 rounded-full text-sm font-medium"
                                            style={{ backgroundColor: '#1a1a1a', color: '#aaa' }}>{c}</span>
                                    ))}
                                </div>
                                <div className="space-y-2 text-sm" style={{ color: '#666' }}>
                                    <div><span className="font-semibold" style={{ color: '#00e5b0' }}>{t('landing.regions.payments')}:</span> {r.payment}</div>
                                    <div><span className="font-semibold" style={{ color: '#00e5b0' }}>{t('landing.regions.currencies')}:</span> {r.currency}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── PRICING ────────────────────────────────────────────────── */}
            <section id="pricing" className="py-28 px-6" style={{ backgroundColor: '#0d0d0d' }}>
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#00e5b0' }}>{t('landing.pricing.title')}</span>
                        <h2 className="text-4xl font-black mt-3 text-white">{t('landing.pricing.title')}</h2>
                        <p className="mt-3" style={{ color: '#666' }}>{t('landing.pricing.subtitle')}</p>
                    </div>
                    <div className="flex justify-center mb-10">
                        <div className="flex items-center gap-1 p-1 rounded-xl" style={{ backgroundColor: '#111', border: '1px solid #1a1a1a' }}>
                            {['monthly','yearly'].map(b => (
                                <button key={b} onClick={() => setBilling(b)}
                                    className="px-5 py-2 rounded-lg text-sm font-semibold transition"
                                    style={billing === b ? { backgroundColor: '#00e5b0', color: '#0a0a0a' } : { color: '#666' }}>
                                    {b === 'monthly' ? t('landing.pricing.monthly') : <span>{t('landing.pricing.yearly')} <span style={{ color: '#00e5b0' }}>-17%</span></span>}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {PLANS_BASE.map((plan) => {
                            const { features, missing } = PLAN_FEATURES[plan.id];
                            return (
                                <div key={plan.id} className="relative p-6 rounded-2xl flex flex-col"
                                    style={{ backgroundColor: plan.highlight ? '#0d2420' : '#111', border: plan.highlight ? '1px solid #00e5b060' : '1px solid #1a1a1a' }}>
                                    {plan.highlight && (
                                        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                                            <span className="px-3 py-1 rounded-full text-xs font-black"
                                                style={{ backgroundColor: '#00e5b0', color: '#0a0a0a' }}>
                                                {t('landing.pricing.popular')}
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2 mb-5">
                                        <span className="text-2xl">{plan.icon}</span>
                                        <h3 className="font-black text-white text-lg">{plan.name}</h3>
                                    </div>
                                    <div className="mb-6">
                                        <span className="text-4xl font-black text-white">{getPrice(plan)}</span>
                                        {plan.priceEur > 0 && (
                                            <span className="text-sm ml-1" style={{ color: '#555' }}>
                                                /{billing === 'yearly' ? t('landing.pricing.per_year') : t('landing.pricing.per_month')}
                                            </span>
                                        )}
                                        {billing === 'yearly' && plan.priceEur > 0 && (
                                            <p className="text-xs mt-1 font-semibold" style={{ color: '#00e5b0' }}>
                                                {t('landing.pricing.free2months')}
                                            </p>
                                        )}
                                    </div>
                                    <ul className="space-y-2 flex-1 mb-6">
                                        {features.map(f => (
                                            <li key={f} className="flex items-start gap-2 text-sm" style={{ color: '#aaa' }}>
                                                <CheckCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#00e5b0' }} /> {f}
                                            </li>
                                        ))}
                                        {missing.map(f => (
                                            <li key={f} className="flex items-start gap-2 text-sm" style={{ color: '#333' }}>
                                                <span className="w-4 h-4 flex-shrink-0 mt-0.5 text-center leading-4">—</span> {f}
                                            </li>
                                        ))}
                                    </ul>
                                    <Link to="/register" className="w-full py-3 rounded-xl text-sm font-black text-center transition block"
                                        style={plan.highlight
                                            ? { backgroundColor: '#00e5b0', color: '#0a0a0a' }
                                            : { border: '1px solid #2a2a2a', color: '#aaa' }}>
                                        {plan.cta_key}
                                    </Link>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ── CTA FINALE ─────────────────────────────────────────────── */}
            <section className="py-28 px-6 relative overflow-hidden">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] rounded-full opacity-10 blur-3xl pointer-events-none"
                    style={{ backgroundColor: '#00e5b0' }} />
                <div className="max-w-3xl mx-auto text-center relative">
                    <div className="flex justify-center mb-6"><Logo size={72} /></div>
                    <h2 className="text-5xl font-black text-white mb-4" style={{ letterSpacing: '-0.03em' }}>{t('landing.cta.title')}</h2>
                    <p className="text-xl mb-10" style={{ color: '#666' }}>{t('landing.cta.subtitle')}</p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to="/register" className="px-8 py-4 text-lg font-black rounded-2xl transition"
                            style={{ backgroundColor: '#00e5b0', color: '#0a0a0a', boxShadow: '0 0 40px rgba(0,229,176,0.2)' }}>
                            {t('landing.cta.btn1')}
                        </Link>
                        <a href="https://wa.me/393899392866" target="_blank" rel="noopener noreferrer"
                            className="px-8 py-4 text-lg font-semibold rounded-2xl transition flex items-center gap-2 justify-center"
                            style={{ border: '1px solid #2a2a2a', color: '#aaa' }}>
                            💬 {t('landing.cta.btn2')}
                        </a>
                    </div>
                    <p className="text-sm mt-6" style={{ color: '#444' }}>{t('landing.cta.fine')}</p>
                </div>
            </section>

            {/* ── FOOTER ─────────────────────────────────────────────────── */}
            <footer style={{ backgroundColor: '#080808', borderTop: '1px solid #1a1a1a' }} className="py-14 px-6">
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-10">
                        <div className="md:col-span-2">
                            <div className="flex items-center gap-3 mb-4">
                                <Logo size={34} />
                                <span className="text-white text-lg font-black">InfraPilot</span>
                            </div>
                            <p className="text-sm leading-relaxed max-w-xs" style={{ color: '#555' }}>
                                {t('landing.regions.subtitle')}
                            </p>
                            <div className="mt-5 flex gap-2 flex-wrap">
                                {['🌊 Wave', '🟠 Orange Money', '💳 Stripe'].map(p => (
                                    <span key={p} className="px-3 py-1 rounded-full text-xs"
                                        style={{ backgroundColor: '#111', color: '#666', border: '1px solid #1a1a1a' }}>{p}</span>
                                ))}
                            </div>
                        </div>
                        <div>
                            <h4 className="text-white font-bold mb-4 text-sm">{t('landing.footer.product')}</h4>
                            <ul className="space-y-2 text-sm" style={{ color: '#555' }}>
                                <li><a href="#features" className="hover:text-white transition">{t('landing.features.title')}</a></li>
                                <li><a href="#pricing" className="hover:text-white transition">{t('landing.pricing.title')}</a></li>
                                <li><a href="#regions" className="hover:text-white transition">{t('landing.regions.title')}</a></li>
                                <li><Link to="/register" className="hover:text-white transition">{t('landing.footer.free_trial')}</Link></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-white font-bold mb-4 text-sm">{t('landing.footer.access')}</h4>
                            <ul className="space-y-2 text-sm" style={{ color: '#555' }}>
                                <li><Link to="/login" className="hover:text-white transition">{t('auth.login')}</Link></li>
                                <li><Link to="/register" className="hover:text-white transition">{t('auth.register')}</Link></li>
                                <li><a href="mailto:goumouraphael57@gmail.com" className="hover:text-white transition">📧 {t('landing.footer.support')}</a></li>
                                <li><a href="https://wa.me/393899392866" target="_blank" rel="noopener noreferrer" className="hover:text-white transition">💬 {t('landing.footer.whatsapp')}</a></li>
                            </ul>
                        </div>
                    </div>
                    <div className="pt-6 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs"
                        style={{ borderTop: '1px solid #1a1a1a', color: '#333' }}>
                        <a href="https://www.producthunt.com/products/infrapilot-4?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-infrapilot-4" target="_blank" rel="noopener noreferrer"><img alt="InfraPilot on Product Hunt" width="250" height="54" src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1167109&theme=dark&t=1781261347455" /></a>
                        <span>© 2026 InfraPilot · {t('landing.footer.rights')} · pilotinfra.app</span>
                        <LangBar />
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;
